import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import {
  GET_PRODUCT_WITH_VARIANTS, CREATE_PRODUCT_VARIANT, UPDATE_PRODUCT_VARIANT, DELETE_PRODUCT_VARIANT,
  UPLOAD_PRODUCT_VARIANT_MEDIA_BASE64,
  type ManageProductWithVariants, type ManagedVariant,
} from '../../graphql/management'
import { Feedback, useFormFeedback, type UserError } from './Feedback'

type Draft = { title: string; price: string; sku: string; inventoryQuantity: string; option1: string; option2: string }
const emptyDraft: Draft = { title: '', price: '', sku: '', inventoryQuantity: '0', option1: '', option2: '' }

// Per-variant colourway images. Reads a File to the data:<mime>;base64,… string
// that uploadProductVariantMediaBase64 expects (same shape as the product one).
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result as string)
    r.onerror = () => reject(r.error)
    r.readAsDataURL(file)
  })
}

function toDraft(v: ManagedVariant): Draft {
  return {
    title: v.title, price: String(v.price), sku: v.sku ?? '',
    inventoryQuantity: String(v.inventoryQuantity), option1: v.option1 ?? '', option2: v.option2 ?? '',
  }
}
function vars(d: Draft) {
  return {
    title: d.title, price: parseFloat(d.price) || 0, sku: d.sku || null,
    inventoryQuantity: parseInt(d.inventoryQuantity, 10) || 0,
    option1: d.option1 || null, option2: d.option2 || null,
  }
}

export default function ProductVariants() {
  const { id } = useParams()
  const navigate = useNavigate()
  const fb = useFormFeedback()
  const { data, loading, refetch } = useQuery<ManageProductWithVariants>(GET_PRODUCT_WITH_VARIANTS, {
    variables: { id }, fetchPolicy: 'cache-and-network',
  })
  const [createVariant, { loading: creating }] = useMutation(CREATE_PRODUCT_VARIANT)
  const [updateVariant] = useMutation(UPDATE_PRODUCT_VARIANT)
  const [deleteVariant] = useMutation(DELETE_PRODUCT_VARIANT)
  const [uploadVariantMedia] = useMutation(UPLOAD_PRODUCT_VARIANT_MEDIA_BASE64)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft>(emptyDraft)
  const [adding, setAdding] = useState<Draft>(emptyDraft)
  const [uploadingId, setUploadingId] = useState<string | null>(null)

  const product = data?.product
  const variants = product?.productVariants ?? []

  async function run(action: () => Promise<UserError[] | undefined>) {
    fb.reset()
    try {
      const errs = await action()
      if (!fb.fromUserErrors(errs)) return
      await refetch(); setEditingId(null)
    } catch (err) { fb.setError(err instanceof Error ? err.message : String(err)) }
  }

  const onAdd = (e: React.FormEvent) => { e.preventDefault(); run(async () => {
    const { data } = await createVariant({ variables: { productId: id, ...vars(adding) } })
    setAdding(emptyDraft)
    return data?.createProductVariant?.userErrors
  })}
  const onSaveEdit = (vid: string) => run(async () => {
    const { data } = await updateVariant({ variables: { id: vid, ...vars(draft) } })
    return data?.updateProductVariant?.userErrors
  })
  const onDelete = (vid: string) => {
    if (variants.length <= 1) { fb.setError('A product must keep at least one variant.'); return }
    if (!window.confirm('Delete this variant?')) return
    run(async () => {
      const { data } = await deleteVariant({ variables: { id: vid } })
      return data?.deleteProductVariant?.userErrors
    })
  }

  async function onUploadImage(vid: string, e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0) return
    fb.reset(); setUploadingId(vid)
    try {
      const tooBig = files.find((f) => f.size > 6 * 1024 * 1024)
      if (tooBig) { fb.setError(`${tooBig.name} is over 6MB.`); return }
      const images = await Promise.all(files.map(fileToDataUrl))
      const { data } = await uploadVariantMedia({ variables: { variantId: vid, images } })
      if (!fb.fromUserErrors(data?.uploadProductVariantMediaBase64?.userErrors as UserError[] | undefined)) return
      await refetch(); fb.setSuccess('Variant image uploaded.')
    } catch (err) { fb.setError(err instanceof Error ? err.message : String(err)) }
    finally { setUploadingId(null); e.target.value = '' }
  }

  if (loading && !product) return <div className="muted">Loading…</div>
  if (!product) return <div className="empty-state"><h2>Product not found</h2></div>

  return (
    <div className="manage-page">
      <header className="manage-header">
        <h1>Variants · {product.title}</h1>
        <button className="btn-link" onClick={() => navigate('/manage/products')}>← Back</button>
      </header>
      <Feedback error={fb.error} success={fb.success} />

      <ul className="variant-list">
        {variants.map((v) => editingId === v.id ? (
          <li key={v.id} className="variant-row variant-row--edit">
            <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Title" />
            <input value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} placeholder="Price" inputMode="decimal" />
            <input value={draft.sku} onChange={(e) => setDraft({ ...draft, sku: e.target.value })} placeholder="SKU" />
            <input value={draft.inventoryQuantity} onChange={(e) => setDraft({ ...draft, inventoryQuantity: e.target.value })} placeholder="Stock" inputMode="numeric" />
            <button className="btn-primary" onClick={() => onSaveEdit(v.id)}>Save</button>
            <button className="btn-link" onClick={() => setEditingId(null)}>Cancel</button>
          </li>
        ) : (
          <li key={v.id} className="variant-row variant-row--view">
            <div className="variant-row-main">
              <span className="variant-title">{v.title}</span>
              <span>{v.price}</span>
              <span className="muted small">{v.sku || '—'}</span>
              <span className="muted small">{v.inventoryQuantity} in stock</span>
              <button className="btn-link" onClick={() => { setEditingId(v.id); setDraft(toDraft(v)) }}>Edit</button>
              <button className="btn-link" onClick={() => onDelete(v.id)}>Delete</button>
            </div>
            <div className="variant-images">
              {(v.imageUrls ?? []).map((u) => <img key={u} src={u} alt="" className="variant-thumb" />)}
              <label className="btn-link file-btn">
                {uploadingId === v.id ? 'Uploading…' : '+ Image'}
                <input type="file" accept="image/*" multiple hidden disabled={uploadingId === v.id}
                  onChange={(e) => onUploadImage(v.id, e)} />
              </label>
            </div>
          </li>
        ))}
      </ul>

      <form className="create-form variant-add" onSubmit={onAdd}>
        <h2 className="manage-subhead">Add variant</h2>
        <div className="variant-add-grid">
          <input value={adding.title} onChange={(e) => setAdding({ ...adding, title: e.target.value })} placeholder="Title (e.g. Black / 42)" required />
          <input value={adding.price} onChange={(e) => setAdding({ ...adding, price: e.target.value })} placeholder="Price" inputMode="decimal" required />
          <input value={adding.sku} onChange={(e) => setAdding({ ...adding, sku: e.target.value })} placeholder="SKU" />
          <input value={adding.inventoryQuantity} onChange={(e) => setAdding({ ...adding, inventoryQuantity: e.target.value })} placeholder="Stock" inputMode="numeric" />
          <input value={adding.option1} onChange={(e) => setAdding({ ...adding, option1: e.target.value })} placeholder="Option 1 (colour)" />
          <input value={adding.option2} onChange={(e) => setAdding({ ...adding, option2: e.target.value })} placeholder="Option 2 (size)" />
        </div>
        <div className="form-actions">
          <button className="btn-primary" type="submit" disabled={creating}>{creating ? 'Adding…' : 'Add variant'}</button>
        </div>
      </form>
    </div>
  )
}
